import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import './Blog.css';

function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

const BlogPost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [forbidden, setForbidden] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError('');
        setForbidden(false);
        setPost(null);

        fetch(`/api/blog/${encodeURIComponent(slug)}`, { credentials: 'include' })
            .then(async r => {
                const data = r.headers.get('content-type')?.includes('application/json')
                    ? await r.json()
                    : {};
                if (r.status === 403) {
                    setForbidden(true);
                    setPost({ title: data.title || slug, visibility: data.visibility || 'members' });
                    return;
                }
                if (!r.ok) throw new Error(data.error || 'Post not found');
                setPost(data);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [slug, user]);

    if (loading || authLoading) {
        return (
            <div className="blog">
                <p>Loading…</p>
            </div>
        );
    }

    if (error && !forbidden) {
        return (
            <div className="blog">
                <div className="blog-header">
                    <h1>Post not found</h1>
                    <button type="button" className="blog-back-button" onClick={() => navigate('/blog')}>
                        Back to blog
                    </button>
                </div>
                <div className="alert-error">{error}</div>
            </div>
        );
    }

    if (forbidden) {
        return (
            <div className="blog">
                <div className="blog-header">
                    <h1>{post?.title || 'Members only'}</h1>
                    <button type="button" className="blog-back-button" onClick={() => navigate('/blog')}>
                        Back to blog
                    </button>
                </div>
                <div className="blog-login-prompt">
                    {user ? (
                        <p>You do not have access to this post.</p>
                    ) : (
                        <p>
                            This post is for logged-in members.{' '}
                            <Link to="/login">Log in</Link> to read it.
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="blog">
            <div className="blog-header">
                <h1>{post.title}</h1>
                <button type="button" className="blog-back-button" onClick={() => navigate('/blog')}>
                    Back to blog
                </button>
            </div>
            <div className="blog-list-meta">
                <span>{formatDate(post.published_at)}</span>
                {post.author_username && <span>by {post.author_username}</span>}
                {post.visibility === 'members' && (
                    <span className="blog-badge blog-badge-members">Members only</span>
                )}
            </div>
            <div className="blog-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {post.body}
                </ReactMarkdown>
            </div>
        </div>
    );
};

export default BlogPost;
